import { defineStore } from "pinia";
import { Escalation, Guarantee, PaySurvivingDependant, TaxFree } from "@/entities/quote";
import { ValueOrUndefined } from "@/utils";

export enum PaymentArrears {
    InArrears = "in-arrears",
    InAdvance = "in-advance",
}

export enum PaymentFrequency {
    Monthly = "monthly",
    Quarterly = "quarterly",
    HalfYearly = "halfYearly",
    Yearly = "annually",
}

export enum ValueProtectOrGuarantee {
    ValueProtection = "value-protection",
    GuaranteePeriod = "guarantee-period",
    None = "none",
}

export enum EscalationType {
    Fixed = "fixed",
    LimitedPriceIndexation = "limited-price-indexation",
    RetailPriceIndex = "retail-price-index",
    None = "none",
}

export enum PaySurvivingDependantType {
    Fraction = "fraction",
    None = "none",
}

export enum TaxFreeType {
    Full = "full",
    Absolute = "absolute",
}

export type PensionOptionsStore = {
    pensionType: boolean | null,
    fundTransferred: boolean | null,
    guaranteeAnnuityRate: boolean | null,
    moreThanTwentyFive: boolean | null,
    totalFund: number | null,
    taxFreeSumFull: boolean | null,
    annuityExceedMaximum: boolean | null,
    acknowledgementILSA: boolean | null,
    escalation: boolean | null,
    paymentFrequency: PaymentFrequency | null,
    paymentArrears: PaymentArrears | null,
    spouseDependant: boolean | null,
    valueProtectOrGuarantee: ValueProtectOrGuarantee | null,
    taxFreeSumSpecific: number | null,
    taxFreeMaxPercent: number,
    escalationType: EscalationType | null,
    escalationAmount: string,
    proportional: boolean | undefined,
    spouseDependantPercent: string
    valueProtectionBasis: string
    valueProtectionPercent: string
    guaranteePeriodYears: string
    overlap: boolean | undefined,
    toggleValueProtectionModal: boolean
    toggleGuaranteePeriodModal: boolean
}

export const usePensionOptionsStore = defineStore("pensionOptions", {
    state: () : PensionOptionsStore => {
        return {
            pensionType: null,
            fundTransferred: null,
            guaranteeAnnuityRate: null,
            moreThanTwentyFive: null,
            totalFund: null,
            taxFreeSumFull: null,
            annuityExceedMaximum: null,
            acknowledgementILSA: null,
            escalation: null,
            paymentFrequency: null,
            paymentArrears: null,
            spouseDependant: null,
            valueProtectOrGuarantee: null,
            taxFreeSumSpecific: null,
            taxFreeMaxPercent: 25,
            escalationType: null,
            escalationAmount: "",
            proportional: undefined,
            spouseDependantPercent: "",
            valueProtectionBasis: "",
            valueProtectionPercent: "",
            guaranteePeriodYears: "",
            overlap: undefined,
            toggleValueProtectionModal: false,
            toggleGuaranteePeriodModal: false,
        };
    },
    getters: {
        totalFundNumber(state): number {
            return state.totalFund ?? 0;
        },
        taxFreeMaxAmount(state): number {
            return this.totalFundNumber > 0
                ? this.totalFundNumber * (state.taxFreeMaxPercent / 100)
                : 0;
        },
        taxFreeSumSpecificNumber: (state): number => state.taxFreeSumSpecific ?? 0,
        getProductInformation(state): Partial<{
            totalFund: number | undefined,
            taxFree: TaxFree,
            annuityExceedMaximum: boolean | null,
            acknowledgementILSA: boolean | null,
            escalation: Escalation,
            paymentFrequency: PaymentFrequency | null,
            proportional: boolean | undefined,
            paymentInArrears: PaymentArrears | null,
            paySurvivingDependant: PaySurvivingDependant,
            overlap: boolean | undefined,
            guarantee: Guarantee,
            pensionType: boolean,
            fundTransferred: boolean,
            guaranteeAnnuityRate: boolean,
            moreThanTwentyFive: boolean,
        }> {
            return {
                totalFund: this.totalFundNumber,
                taxFree: state.taxFreeSumFull === null
                    ? undefined
                    : state.taxFreeSumFull
                        ? {
                            type: TaxFreeType.Full,
                            fraction: state.taxFreeMaxPercent,
                            value: this.taxFreeMaxAmount,
                        }
                        : {
                            type: TaxFreeType.Absolute,
                            value: this.taxFreeSumSpecificNumber,
                        },
                annuityExceedMaximum: state.annuityExceedMaximum,
                acknowledgementILSA: state.acknowledgementILSA,
                escalation: state.escalation === null ? undefined : mapEscalation(state),
                paymentFrequency: ValueOrUndefined(state.paymentFrequency, null),
                proportional: state.proportional,
                paymentInArrears: state.paymentArrears,
                paySurvivingDependant: state.spouseDependant === null
                    ? undefined
                    : !state.spouseDependant
                        ? {
                            type: PaySurvivingDependantType.None,
                        }
                        : {
                            type: PaySurvivingDependantType.Fraction,
                            value: Number(state.spouseDependantPercent),
                        },
                overlap: state.overlap,
                guarantee: mapGuarantee(state),
                pensionType: state.pensionType ?? undefined,
                fundTransferred: state.fundTransferred ?? undefined,
                guaranteeAnnuityRate: state.guaranteeAnnuityRate ?? undefined,
                moreThanTwentyFive: state.moreThanTwentyFive ?? undefined,
            };
        },
    },
});

const mapGuarantee = (state: PensionOptionsStore) : Guarantee | undefined => {
    if (!state.valueProtectOrGuarantee) {
        return undefined;
    }

    switch (state.valueProtectOrGuarantee) {
        case ValueProtectOrGuarantee.None:
            return { type: ValueProtectOrGuarantee.None };
        case ValueProtectOrGuarantee.GuaranteePeriod:
            return { type: ValueProtectOrGuarantee.GuaranteePeriod, years: Number(state.guaranteePeriodYears) };
        case ValueProtectOrGuarantee.ValueProtection:
            return {
                type: ValueProtectOrGuarantee.ValueProtection,
                fraction: Number(state.valueProtectionPercent),
                endingAt: state.spouseDependant ? state.valueProtectionBasis : "my-death",
            };
        default:
            throw new Error("Unable to map to Guarantee type");
    }
};

const mapEscalation = (state: PensionOptionsStore) : Escalation => {
    if (!state.escalation || !state.escalationType) {
        return { type: EscalationType.None };
    }
    switch (state.escalationType) {
        case EscalationType.Fixed:
            return { type: EscalationType.Fixed, value: Number(state.escalationAmount) };
        case EscalationType.LimitedPriceIndexation:
        case EscalationType.RetailPriceIndex:
        case EscalationType.None:
            return { type: state.escalationType };
        default:
            throw new Error("Unable to map to Escalation type");
    }
};
